import { PaperSize } from "@client-shared/types/pdfPrint";
import { degrees, PDFDocument, PDFFont, PDFPage, rgb, StandardFonts } from "pdf-lib";
import { Dimensions, getDimensions, getScaledFontSizeForSmallPaper } from "./packingSlipGenerator/utils/dimensionUtils";
import { normalizeTextForWinAnsi } from "./packingSlipGenerator/utils/textUtils";
import { fetchPdfArrayBuffer } from "./pdfFetchUtils";
import { serializedPdfLoad } from "./pdfMerger";

/**
 * GLOBAL PDF-LIB OPERATION QUEUE
 *
 * pdf-lib 1.17.1 has a known bug (Issue #1389) where concurrent operations can corrupt
 * internal validation state, causing valid PDFDocuments to appear as NaN. We serialize
 * ALL pdf-lib operations (create, load, copyPages, save, addPage, etc.) through a single
 * global queue to prevent concurrent access and state corruption.
 */
let globalPdfLibQueue: Promise<void> = Promise.resolve();

/**
 * Track NaN errors to detect if pdf-lib's state is persistently corrupted.
 * Since all operations are serialized through the global queue, NaN errors should be rare.
 * If they persist, it indicates a deeper issue with pdf-lib or the PDF files themselves.
 */
let nanErrorCount = 0;
const MAX_NAN_ERRORS_BEFORE_WARNING = 10;

// Retry configuration for pdf-lib NaN errors
const INITIAL_RETRY_DELAY_MS = 50; // Initial delay for first retry
const MAX_RETRY_DELAY_MS = 500; // Maximum delay cap
const RETRY_BACKOFF_MULTIPLIER = 2; // Exponential backoff multiplier

/**
 * Reset internal diagnostics and queue state.
 *
 * This is intended for use in tests to avoid cross-test pollution from module-scoped state.
 * It should not be called in normal application code.
 */
export function resetPdfLibDiagnosticsForTesting(): void {
  nanErrorCount = 0;
  globalPdfLibQueue = Promise.resolve();
}

/**
 * Executes a pdf-lib operation through the global queue to ensure sequential execution.
 * This prevents any concurrent pdf-lib operations that could corrupt internal state.
 * Exported so pdfMerger.ts can use the same queue.
 *
 * @param operation - The pdf-lib operation to execute
 * @returns The result of the operation
 */
export async function executeInGlobalPdfLibQueue<T>(operation: () => Promise<T>): Promise<T> {
  let operationResult: T | undefined;
  let operationError: Error | undefined;

  globalPdfLibQueue = globalPdfLibQueue
    .then(async () => {
      try {
        operationResult = await operation();
      } catch (error) {
        operationError = error instanceof Error ? error : new Error(String(error));
      }
    })
    .catch(async () => {
      // If queue fails, still try to execute the operation
      try {
        operationResult = await operation();
      } catch (error) {
        operationError = error instanceof Error ? error : new Error(String(error));
      }
    });

  await globalPdfLibQueue;

  if (operationError) {
    throw operationError;
  }

  if (operationResult === undefined) {
    throw new Error("Operation returned undefined");
  }

  return operationResult;
}

/**
 * Creates a serialized PDFDocument.create function that queues create operations to prevent race conditions.
 * pdf-lib's PDFDocument.create may not be safe for concurrent calls, especially when combined with
 * other pdf-lib operations. Serializing all PDFDocument.create calls prevents internal state corruption.
 *
 * @returns A function that queues and executes PDFDocument.create operations sequentially
 */
const createSerializedPdfCreate = (): (() => Promise<PDFDocument>) => {
  return async (): Promise<PDFDocument> => {
    return await executeInGlobalPdfLibQueue(async () => {
      return await PDFDocument.create();
    });
  };
};

// Create a single shared serialized PDF creator instance
// This ensures all PDFDocument.create calls are serialized globally
// Export it so other modules can use the same global queue
export const serializedPdfCreate = createSerializedPdfCreate();

/**
 * Validates that srcDoc pages can be accessed safely.
 *
 * @param srcDoc - Source PDFDocument to validate.
 * @throws TypeError if pages cannot be accessed or returned invalid results.
 */
function validateSrcDocPagesAccessible(srcDoc: PDFDocument): void {
  try {
    const testPages = srcDoc.getPages();
    if (!Array.isArray(testPages)) {
      throw new TypeError(`srcDoc.getPages() returned non-array: ${typeof testPages}`);
    }
  } catch (accessError) {
    throw new TypeError(`Failed to access srcDoc.getPages(): ${accessError instanceof Error ? accessError.message : String(accessError)}`);
  }
}

/**
 * Validates that destDoc can perform copyPages.
 *
 * @param destDoc - Destination PDFDocument to validate.
 * @throws TypeError if destDoc is invalid.
 */
function validateDestDoc(destDoc: PDFDocument): void {
  if (!destDoc || typeof destDoc !== "object" || typeof destDoc.copyPages !== "function") {
    throw new TypeError(`Invalid destDoc: ${typeof destDoc}`);
  }
}

/**
 * Validates that page indices are an array of non-negative numbers.
 *
 * @param pageIndices - Page indices to validate.
 * @throws TypeError if page indices are invalid.
 */
function validateCopyPageIndices(pageIndices: number[]): void {
  if (!Array.isArray(pageIndices)) {
    throw new TypeError(`pageIndices is not an array: ${typeof pageIndices}`);
  }
  for (const idx of pageIndices) {
    if (typeof idx !== "number" || !Number.isFinite(idx) || idx < 0) {
      throw new TypeError(`Invalid page index: ${idx}`);
    }
  }
}

/**
 * Validates inputs before running copyPages.
 *
 * @param destDoc - Destination PDFDocument.
 * @param srcDoc - Source PDFDocument.
 * @param pageIndices - Page indices to copy.
 */
function validateCopyPagesInputs(destDoc: PDFDocument, srcDoc: PDFDocument, pageIndices: number[]): void {
  validatePdfDocument(srcDoc, "srcDoc before copyPages");
  validateSrcDocPagesAccessible(srcDoc);
  validateDestDoc(destDoc);
  validateCopyPageIndices(pageIndices);
}

/**
 * Computes the retry delay using exponential backoff.
 *
 * @param attemptCount - Current retry attempt count.
 * @returns Delay in milliseconds.
 */
function getRetryDelayMs(attemptCount: number): number {
  return Math.min(INITIAL_RETRY_DELAY_MS * Math.pow(RETRY_BACKOFF_MULTIPLIER, attemptCount - 1), MAX_RETRY_DELAY_MS);
}

/**
 * Logs and tracks persistent NaN errors from pdf-lib copyPages.
 *
 * @param maxAttempts - Maximum retry attempts allowed.
 */
function handlePersistentNaNError(maxAttempts: number): void {
  nanErrorCount++;
  console.error(
    `[copyPages] NaN error persists after ${maxAttempts} attempts. ` + `Skipping this PDF. (Total NaN errors: ${nanErrorCount})`
  );

  if (nanErrorCount >= MAX_NAN_ERRORS_BEFORE_WARNING && nanErrorCount === MAX_NAN_ERRORS_BEFORE_WARNING) {
    console.warn(
      `[copyPages] WARNING: ${nanErrorCount} NaN errors detected. ` +
        `This may indicate corrupted PDF files or pdf-lib issues. ` +
        `All pdf-lib operations are serialized to prevent race conditions.`
    );
  }
}

/**
 * Attempts to copy pages with retry logic for known pdf-lib NaN errors.
 *
 * @param destDoc - Destination PDFDocument.
 * @param srcDoc - Source PDFDocument.
 * @param pageIndices - Page indices to copy.
 * @returns Copied pages.
 * @throws Error when copyPages fails after retries.
 */
async function copyPagesWithRetry(
  destDoc: PDFDocument,
  srcDoc: PDFDocument,
  pageIndices: number[]
): Promise<Awaited<ReturnType<PDFDocument["copyPages"]>>> {
  const maxAttempts = 2;
  let attemptCount = 0;

  while (attemptCount < maxAttempts) {
    try {
      // Retry attempts must run one after another.
      return await destDoc.copyPages(srcDoc, pageIndices); // NOSONAR
    } catch (copyError) {
      attemptCount++;

      if (!isNaNError(copyError)) {
        throw copyError;
      }

      if (attemptCount < maxAttempts) {
        const delayMs = getRetryDelayMs(attemptCount);
        // Backoff delay between retry attempts.
        await new Promise((resolve) => setTimeout(resolve, delayMs)); // NOSONAR
        continue;
      }

      handlePersistentNaNError(maxAttempts);
      throw copyError;
    }
  }

  throw new Error("copyPages failed after all retry attempts");
}

/**
 * Creates a serialized copyPages function that queues operations to prevent race conditions.
 * Serializes all copyPages calls through the global queue to prevent pdf-lib state corruption.
 *
 * @returns A function that queues and executes copyPages operations sequentially
 */
const createSerializedCopyPages = (): ((
  destDoc: PDFDocument,
  srcDoc: PDFDocument,
  pageIndices: number[]
) => Promise<Awaited<ReturnType<PDFDocument["copyPages"]>>>) => {
  return async (
    destDoc: PDFDocument,
    srcDoc: PDFDocument,
    pageIndices: number[]
  ): Promise<Awaited<ReturnType<PDFDocument["copyPages"]>>> => {
    return await executeInGlobalPdfLibQueue(async () => {
      validateCopyPagesInputs(destDoc, srcDoc, pageIndices);
      return await copyPagesWithRetry(destDoc, srcDoc, pageIndices);
    });
  };
};

// Create a single shared serialized copyPages instance
// This ensures all copyPages calls are serialized globally
// Export it so other modules can use the same global queue
export const serializedCopyPages = createSerializedCopyPages();

// Rotation constants
const ROTATION_90_DEGREES = 90;
const ROTATION_270_DEGREES = 270;
const ROTATION_COUNTER_CLOCKWISE_90 = -90;
const FULL_ROTATION_DEGREES = 360;

// Dimension matching tolerances
const DIMENSION_TOLERANCE = 5; // Used for approximate dimension matching (e.g., implicit rotation detection)
const EXACT_DIMENSION_TOLERANCE = 1; // Used for exact dimension matching (e.g., skip processing check)

/**
 * Validates that a value is a valid PDFDocument instance.
 * @param doc - The value to validate
 * @param context - Context string for error messages
 * @throws TypeError if doc is not a valid PDFDocument
 */
function validatePdfDocument(doc: unknown, context: string): asserts doc is PDFDocument {
  if (!doc || typeof doc !== "object") {
    throw new TypeError(`${context}: Invalid PDFDocument (${typeof doc})`);
  }
  if (typeof (doc as PDFDocument).getPages !== "function") {
    throw new TypeError(`${context}: Missing getPages method`);
  }
}

/**
 * Checks if an error is a NaN-related error from pdf-lib.
 * @param error - The error to check
 * @returns true if the error is NaN-related
 */
function isNaNError(error: unknown): boolean {
  if (!(error instanceof Error)) {
    return false;
  }
  const msg = error.message.toLowerCase();
  return msg.includes("nan") || msg.includes("type nan");
}

/**
 * Validates that dimensions are valid numbers.
 * @param width - Width to validate
 * @param height - Height to validate
 * @param context - Context string for error messages
 * @throws Error if dimensions are invalid
 */
function validateDimensions(width: number, height: number, context: string): void {
  if (!Number.isFinite(width) || !Number.isFinite(height) || width <= 0 || height <= 0) {
    throw new Error(`${context}: Invalid dimensions (${width}x${height})`);
  }
}

/**
 * Validates that margin is a non-negative finite number.
 *
 * @param margin - Margin to validate.
 * @throws Error when margin is invalid.
 */
function validateMargin(margin: number): void {
  if (!Number.isFinite(margin) || margin < 0) {
    throw new Error(`Invalid margin: ${margin}`);
  }
}

/**
 * Retrieves the first page from a PDFDocument or throws if none exist.
 *
 * @param srcDoc - Source PDFDocument.
 * @returns The first PDFPage.
 * @throws Error when the PDF has no pages.
 */
function getFirstPageOrThrow(srcDoc: PDFDocument): PDFPage {
  const pages = srcDoc.getPages();
  if (pages.length === 0 || !pages[0]) {
    throw new Error("Source PDF has no pages");
  }
  return pages[0];
}

/**
 * Validates that the source page dimensions are finite and positive.
 *
 * @param width - Source page width.
 * @param height - Source page height.
 * @throws Error when dimensions are invalid.
 */
function validateSourcePageDimensions(width: number, height: number): void {
  if (!Number.isFinite(width) || !Number.isFinite(height) || width <= 0 || height <= 0) {
    throw new Error(`PDF has corrupted dimensions (width=${width}, height=${height}). Cannot process PDF with invalid dimensions.`);
  }
}

/**
 * Calculates the scale factor needed to fit a PDF page within target dimensions
 * while maintaining aspect ratio. Only scales down if needed.
 */
const calculateScaleFactor = (srcWidth: number, srcHeight: number, targetWidth: number, targetHeight: number): number => {
  if (srcWidth <= 0 || srcHeight <= 0 || targetWidth <= 0 || targetHeight <= 0) {
    return 1;
  }
  return srcWidth > targetWidth || srcHeight > targetHeight ? Math.min(targetWidth / srcWidth, targetHeight / srcHeight) : 1;
};

/**
 * Determines if dimensions are portrait (height >= width) or landscape (width > height).
 */
const isPortrait = (width: number, height: number): boolean => {
  return height >= width;
};

interface RotatedDrawParams {
  drawX: number;
  drawY: number;
  drawWidth: number;
  drawHeight: number;
  drawRotate: ReturnType<typeof degrees>;
}

/**
 * Calculates draw parameters for rotating content 90° clockwise around origin (0,0).
 * When rotating content 90° clockwise around its top-left corner (0,0):
 * - The content rotates around (0,0), so after rotation it extends:
 *   - Rightward by originalHeight (the original vertical dimension)
 *   - Downward by originalWidth (the original horizontal dimension)
 * - To center the rotated content on the page:
 *   - drawX: Start at originalHeight (rotation offset), then add horizontal centering offset.
 *            This positions the rotated content so its visual width (originalHeight) is centered.
 *   - drawY: Center vertically by offsetting by half the difference between page height
 *            and the rotated content's visual height (originalWidth).
 */
function calculateRotatedDrawParams(
  embeddedPage: { width: number; height: number },
  scale: number,
  dimensions: { width: number; height: number }
): RotatedDrawParams {
  const originalWidth = embeddedPage.width * scale;
  const originalHeight = embeddedPage.height * scale;
  const drawX = originalHeight + (dimensions.width - originalHeight) / 2;
  const drawY = (dimensions.height - originalWidth) / 2;
  return {
    drawX,
    drawY,
    drawWidth: originalWidth,
    drawHeight: originalHeight,
    drawRotate: degrees(ROTATION_90_DEGREES),
  };
}

/**
 * Safely extracts rotation angle from pdf-lib's getRotation() return value.
 */
const extractRotationAngle = (rotation: unknown): number => {
  if (typeof rotation === "number") {
    return rotation;
  }
  if (rotation && typeof rotation === "object" && "angle" in rotation) {
    const angle = (rotation as { angle?: unknown }).angle;
    return typeof angle === "number" ? angle : 0;
  }
  return 0;
};

interface RotationAnalysis {
  actualSrcWidth: number;
  actualSrcHeight: number;
  isRotated90or270: boolean;
  hasImplicitRotation: boolean;
  needsRotation: boolean;
  actualOrientationMatchesPaper: boolean;
}

/**
 * Checks if source dimensions match paper dimensions when swapped.
 */
function areDimensionsSwapped(srcWidth: number, srcHeight: number, paperWidth: number, paperHeight: number, tolerance: number): boolean {
  const widthDiffHeight = Math.abs(srcWidth - paperHeight);
  const heightDiffWidth = Math.abs(srcHeight - paperWidth);
  return widthDiffHeight < tolerance && heightDiffWidth < tolerance;
}

/**
 * Checks if source dimensions match paper dimensions directly.
 */
function doDimensionsMatch(srcWidth: number, srcHeight: number, paperWidth: number, paperHeight: number, tolerance: number): boolean {
  const widthDiffPaper = Math.abs(srcWidth - paperWidth);
  const heightDiffPaper = Math.abs(srcHeight - paperHeight);
  return widthDiffPaper < tolerance && heightDiffPaper < tolerance;
}

/**
 * Determines if rotation is needed due to orientation mismatch.
 * The source and paper orientations differ, but swapping the source width/height would match the paper's orientation.
 */
function needsRotationDueToOrientationMismatch(srcWidth: number, srcHeight: number, paperWidth: number, paperHeight: number): boolean {
  const paperIsPortrait = isPortrait(paperWidth, paperHeight);
  const srcIsPortrait = isPortrait(srcWidth, srcHeight);
  const srcIsPortraitIfSwapped = isPortrait(srcHeight, srcWidth);
  const orientationMismatch = srcIsPortrait !== paperIsPortrait;
  const swappedMatchesPaper = srcIsPortraitIfSwapped === paperIsPortrait;
  return orientationMismatch && swappedMatchesPaper;
}

/**
 * Determines if rotation is needed due to swapped dimensions.
 * The source dimensions are close to the paper's dimensions but swapped.
 */
function needsRotationDueToSwappedDimensions(
  srcWidth: number,
  srcHeight: number,
  paperWidth: number,
  paperHeight: number,
  tolerance: number
): boolean {
  const dimensionsSwapped = areDimensionsSwapped(srcWidth, srcHeight, paperWidth, paperHeight, tolerance);
  const dimensionsMatch = doDimensionsMatch(srcWidth, srcHeight, paperWidth, paperHeight, tolerance);
  return dimensionsSwapped && !dimensionsMatch;
}

/**
 * Detects if the source page has an implicit rotation that would require us to rotate it
 * to match the target paper orientation.
 */
function hasImplicitRotation(
  isRotated90or270: boolean,
  srcWidth: number,
  srcHeight: number,
  paperWidth: number,
  paperHeight: number,
  tolerance: number
): boolean {
  if (isRotated90or270) {
    return false;
  }
  const needsRotationDueToOrientation = needsRotationDueToOrientationMismatch(srcWidth, srcHeight, paperWidth, paperHeight);
  const needsRotationDueToSwapped = needsRotationDueToSwappedDimensions(srcWidth, srcHeight, paperWidth, paperHeight, tolerance);
  return needsRotationDueToOrientation || needsRotationDueToSwapped;
}

/**
 * Analyzes PDF page dimensions and rotation to determine if rotation is needed.
 * This handles both explicit rotation metadata and implicit rotation detection.
 *
 * @param srcWidth - Source page width
 * @param srcHeight - Source page height
 * @param rotationAngle - Rotation angle from page metadata
 * @param paperWidth - Target paper width
 * @param paperHeight - Target paper height
 * @returns Analysis object with rotation-related calculations
 */
function analyzeRotation(
  srcWidth: number,
  srcHeight: number,
  rotationAngle: number,
  paperWidth: number,
  paperHeight: number
): RotationAnalysis {
  const normalizedAngle = ((rotationAngle % FULL_ROTATION_DEGREES) + FULL_ROTATION_DEGREES) % FULL_ROTATION_DEGREES;
  const isRotated90or270 = normalizedAngle === ROTATION_90_DEGREES || normalizedAngle === ROTATION_270_DEGREES;

  const actualSrcWidth = isRotated90or270 ? srcHeight : srcWidth;
  const actualSrcHeight = isRotated90or270 ? srcWidth : srcHeight;

  const implicitRotation = hasImplicitRotation(
    isRotated90or270,
    actualSrcWidth,
    actualSrcHeight,
    paperWidth,
    paperHeight,
    DIMENSION_TOLERANCE
  );

  const paperIsPortrait = isPortrait(paperWidth, paperHeight);
  const srcIsPortrait = isPortrait(actualSrcWidth, actualSrcHeight);
  const needsRotationDueToOrientation = !isRotated90or270 && srcIsPortrait !== paperIsPortrait;

  // Check if the actual visual content orientation (after accounting for metadata rotation) matches the paper orientation
  // If orientation matches, we should reset rotation to 0 to avoid double-rotation
  // The scaling logic will handle size differences correctly
  const actualOrientationMatchesPaper = srcIsPortrait === paperIsPortrait;

  /**
   * Determine if rotation is needed. This includes:
   * 1. Implicit rotation (when dimensions are swapped or orientation mismatch can be fixed by rotation)
   * 2. Orientation mismatch (when source and paper orientations don't match and page isn't already rotated)
   * 3. Preserving existing rotation ONLY if the actual visual content orientation doesn't already match the paper
   *    (if orientation matches, we reset rotation to 0 to avoid double-rotation, regardless of dimension matching)
   */
  const needsRotation = implicitRotation || needsRotationDueToOrientation || (isRotated90or270 && !actualOrientationMatchesPaper);

  return {
    actualSrcWidth,
    actualSrcHeight,
    isRotated90or270,
    hasImplicitRotation: implicitRotation,
    needsRotation,
    actualOrientationMatchesPaper,
  };
}

/**
 * Determines if the PDF can be returned as-is without processing.
 *
 * A PO bar has to be drawn onto a fresh output page, so requesting one always forces processing.
 */
function shouldSkipProcessing(srcDoc: PDFDocument, paperSize: PaperSize, margin: number, poNumber?: string): boolean {
  if (poNumber) {
    return false;
  }

  const dimensions = getDimensions(paperSize);
  const pages = srcDoc.getPages();
  const firstPage = pages[0];
  if (!firstPage) {
    return false;
  }

  const srcWidth = firstPage.getWidth();
  const srcHeight = firstPage.getHeight();
  const srcRotation = firstPage.getRotation();
  const rotationAngle = extractRotationAngle(srcRotation);
  const { actualSrcWidth, actualSrcHeight, needsRotation } = analyzeRotation(
    srcWidth,
    srcHeight,
    rotationAngle,
    dimensions.width,
    dimensions.height
  );

  if (needsRotation || margin !== 0) {
    return false;
  }

  const targetWidth = dimensions.width - margin * 2;
  const targetHeight = dimensions.height - margin * 2;
  const widthMatch = Math.abs(actualSrcWidth - targetWidth) < EXACT_DIMENSION_TOLERANCE;
  const heightMatch = Math.abs(actualSrcHeight - targetHeight) < EXACT_DIMENSION_TOLERANCE;

  return widthMatch && heightMatch;
}

/**
 * Checks if processing can be skipped (no rotation, no margin, scale is 1, dimensions match exactly).
 */
function canSkipProcessing(
  needsRotation: boolean,
  margin: number,
  scale: number,
  actualSrcWidth: number,
  actualSrcHeight: number,
  targetWidth: number,
  targetHeight: number
): boolean {
  return (
    !needsRotation &&
    margin === 0 &&
    scale === 1 &&
    Math.abs(actualSrcWidth - targetWidth) < EXACT_DIMENSION_TOLERANCE &&
    Math.abs(actualSrcHeight - targetHeight) < EXACT_DIMENSION_TOLERANCE
  );
}

/**
 * Applies rotation to the source page based on rotation analysis.
 */
function applyRotationToPage(
  srcPageForOutput: { setRotation: (rotation: ReturnType<typeof degrees>) => void },
  needsRotation: boolean,
  isRotated90or270: boolean,
  actualOrientationMatchesPaper: boolean,
  rotationAngle: number
): void {
  if (needsRotation) {
    const rotationDelta = isRotated90or270 ? 0 : ROTATION_90_DEGREES;
    const newRotationAngle = (((rotationAngle + rotationDelta) % FULL_ROTATION_DEGREES) + FULL_ROTATION_DEGREES) % FULL_ROTATION_DEGREES;
    srcPageForOutput.setRotation(degrees(newRotationAngle));
  }

  // If the page has rotation metadata but the visual content orientation already matches the paper,
  // reset rotation to 0 to avoid double-rotation (even if needsRotation was false)
  if (isRotated90or270 && actualOrientationMatchesPaper) {
    srcPageForOutput.setRotation(degrees(0));
  }
}

interface EffectiveDimensions {
  effectiveScale: number;
  effectiveDrawWidth: number;
  effectiveDrawHeight: number;
}

/**
 * Calculates effective scale and draw dimensions based on whether rotation is being reset.
 */
function calculateEffectiveDimensions(
  willResetRotation: boolean,
  actualSrcWidth: number,
  actualSrcHeight: number,
  targetWidth: number,
  targetHeight: number,
  embeddedPage: Awaited<ReturnType<typeof PDFDocument.prototype.embedPage>>,
  scale: number
): EffectiveDimensions {
  if (willResetRotation) {
    // When we reset rotation, the embedded page has original dimensions (e.g., 432x288 landscape)
    // But the visual content is actually the swapped dimensions (e.g., 288x432 portrait)
    // We need to scale based on the visual content dimensions and draw with rotation
    // to match the visual orientation
    const effectiveScale = calculateScaleFactor(actualSrcWidth, actualSrcHeight, targetWidth, targetHeight);
    // Draw dimensions: use the embedded page dimensions scaled to match visual content.
    // The actual swapping of width and height will occur when we apply the counter-clockwise 90° rotation during drawing.
    return {
      effectiveScale,
      effectiveDrawWidth: embeddedPage.width * effectiveScale,
      effectiveDrawHeight: embeddedPage.height * effectiveScale,
    };
  }

  // Use the calculated dimensions from above
  return {
    effectiveScale: scale,
    effectiveDrawWidth: actualSrcWidth * scale,
    effectiveDrawHeight: actualSrcHeight * scale,
  };
}

interface DrawParameters {
  drawX: number;
  drawY: number;
  drawWidth: number;
  drawHeight: number;
  drawRotate: ReturnType<typeof degrees> | undefined;
}

interface CalculateDrawParametersInput {
  needsRotation: boolean;
  isRotated90or270: boolean;
  willResetRotation: boolean;
  embeddedPage: Awaited<ReturnType<typeof PDFDocument.prototype.embedPage>>;
  effectiveScale: number;
  dimensions: { width: number; height: number };
  targetX: number;
  targetY: number;
  effectiveDrawWidth: number;
  effectiveDrawHeight: number;
}

/**
 * Calculates draw parameters for positioning and rotating the embedded page.
 */
function calculateDrawParameters(params: CalculateDrawParametersInput): DrawParameters {
  const {
    needsRotation,
    isRotated90or270,
    willResetRotation,
    embeddedPage,
    effectiveScale,
    dimensions,
    targetX,
    targetY,
    effectiveDrawWidth,
    effectiveDrawHeight,
  } = params;
  if (needsRotation && !isRotated90or270) {
    const rotatedParams = calculateRotatedDrawParams(embeddedPage, effectiveScale, dimensions);
    return {
      drawX: rotatedParams.drawX,
      drawY: rotatedParams.drawY,
      drawWidth: rotatedParams.drawWidth,
      drawHeight: rotatedParams.drawHeight,
      drawRotate: rotatedParams.drawRotate,
    };
  }

  if (willResetRotation) {
    // When we reset rotation, we need to manually rotate the drawing to match visual content orientation
    // The embedded page has original dimensions, but visual content has swapped dimensions
    // We rotate counter-clockwise 90° when drawing to match the visual orientation
    // For counter-clockwise 90° rotation, the positioning is different from clockwise 90° rotation
    const scaledWidth = embeddedPage.width * effectiveScale;
    const scaledHeight = embeddedPage.height * effectiveScale;
    // After counter-clockwise 90° rotation, the visual width is scaledHeight and visual height is scaledWidth
    // For counter-clockwise 90° rotation around (0,0), content extends leftward by scaledHeight and upward by scaledWidth
    // To center: drawX should position the rotated content's right edge, then center horizontally
    // drawY should position the rotated content's bottom edge, then center vertically
    return {
      drawX: (dimensions.width - scaledHeight) / 2,
      drawY: scaledWidth + (dimensions.height - scaledWidth) / 2,
      drawWidth: scaledWidth,
      drawHeight: scaledHeight,
      drawRotate: degrees(ROTATION_COUNTER_CLOCKWISE_90),
    };
  }

  return {
    drawX: targetX,
    drawY: targetY,
    drawWidth: effectiveDrawWidth,
    drawHeight: effectiveDrawHeight,
    drawRotate: undefined,
  };
}

const PO_BAR_FONT_SIZE = 9;
const PO_BAR_FONT_SIZE_SMALL = 7;
const PO_BAR_PADDING = 5;
const PO_BAR_PADDING_SMALL = 3;
const PO_BAR_BORDER_THICKNESS = 0.75;
const PO_BAR_TEXT_RAISE = 2;

/** Stamps the PO number, centred, on an opaque strip along the bottom edge of the page. */
function drawPoNumberBar(page: PDFPage, dimensions: Dimensions, poNumber: string, font: PDFFont): void {
  const text = normalizeTextForWinAnsi(poNumber);
  const fontSize = getScaledFontSizeForSmallPaper(PO_BAR_FONT_SIZE, PO_BAR_FONT_SIZE_SMALL, dimensions);
  const padding = getScaledFontSizeForSmallPaper(PO_BAR_PADDING, PO_BAR_PADDING_SMALL, dimensions);
  const barHeight = fontSize + padding * 2;
  const black = rgb(0, 0, 0);

  page.drawRectangle({ x: 0, y: 0, width: dimensions.width, height: barHeight, color: rgb(1, 1, 1) });
  page.drawRectangle({ x: 0, y: barHeight, width: dimensions.width, height: PO_BAR_BORDER_THICKNESS, color: black });
  page.drawText(text, {
    x: (dimensions.width - font.widthOfTextAtSize(text, fontSize)) / 2,
    y: padding + PO_BAR_TEXT_RAISE,
    size: fontSize,
    font,
    color: black,
  });
}

/**
 * Core logic for fitting a PDF to a specified paper size and margin.
 */
/** Geometry needed to place one source page onto an output page of the target paper size. */
function analyzePageFit(srcPage: PDFPage, dimensions: Dimensions, margin: number) {
  const srcWidth = srcPage.getWidth();
  const srcHeight = srcPage.getHeight();
  validateSourcePageDimensions(srcWidth, srcHeight);

  const rotationAngle = extractRotationAngle(srcPage.getRotation());
  const { actualSrcWidth, actualSrcHeight, isRotated90or270, needsRotation, actualOrientationMatchesPaper } = analyzeRotation(
    srcWidth,
    srcHeight,
    rotationAngle,
    dimensions.width,
    dimensions.height
  );

  // Orientation matches but the page carries rotation metadata, so the rotation gets reset.
  const willResetRotation = isRotated90or270 && actualOrientationMatchesPaper;

  const targetWidth = dimensions.width - margin * 2;
  const targetHeight = dimensions.height - margin * 2;
  // When resetting rotation the embedded page keeps its original (unswapped) dimensions.
  const finalSrcWidth = needsRotation && !willResetRotation ? actualSrcHeight : actualSrcWidth;
  const finalSrcHeight = needsRotation && !willResetRotation ? actualSrcWidth : actualSrcHeight;
  const scale = calculateScaleFactor(finalSrcWidth, finalSrcHeight, targetWidth, targetHeight);

  return {
    rotationAngle,
    actualSrcWidth,
    actualSrcHeight,
    isRotated90or270,
    needsRotation,
    actualOrientationMatchesPaper,
    willResetRotation,
    targetWidth,
    targetHeight,
    scale,
  };
}

/** Draws one copied source page, scaled and centred, onto its output page. */
async function drawFittedPage(
  outDoc: PDFDocument,
  outPage: PDFPage,
  copiedPage: Awaited<ReturnType<PDFDocument["copyPages"]>>[0],
  fit: ReturnType<typeof analyzePageFit>,
  dimensions: Dimensions,
  margin: number
): Promise<void> {
  const { needsRotation, isRotated90or270, actualOrientationMatchesPaper, rotationAngle, willResetRotation } = fit;

  applyRotationToPage(copiedPage, needsRotation, isRotated90or270, actualOrientationMatchesPaper, rotationAngle);

  // Serialize embedPage through global queue to prevent corruption
  const embeddedPage = await executeInGlobalPdfLibQueue(async () => {
    return await outDoc.embedPage(copiedPage);
  });

  const { effectiveScale, effectiveDrawWidth, effectiveDrawHeight } = calculateEffectiveDimensions(
    willResetRotation,
    fit.actualSrcWidth,
    fit.actualSrcHeight,
    fit.targetWidth,
    fit.targetHeight,
    embeddedPage,
    fit.scale
  );

  const targetX = margin + (dimensions.width - margin * 2 - effectiveDrawWidth) / 2;
  const targetY = margin + (dimensions.height - margin * 2 - effectiveDrawHeight) / 2;

  const { drawX, drawY, drawWidth, drawHeight, drawRotate } = calculateDrawParameters({
    needsRotation,
    isRotated90or270,
    willResetRotation,
    embeddedPage,
    effectiveScale,
    dimensions,
    targetX,
    targetY,
    effectiveDrawWidth,
    effectiveDrawHeight,
  });

  outPage.drawPage(embeddedPage, {
    x: drawX,
    y: drawY,
    width: drawWidth,
    height: drawHeight,
    ...(drawRotate && { rotate: drawRotate }),
  });
}

/** True when the source already fits the target exactly, so it can be returned untouched. */
function canPassThrough(fit: ReturnType<typeof analyzePageFit>, margin: number, poNumber?: string): boolean {
  if (poNumber) {
    return false;
  }
  return canSkipProcessing(
    fit.needsRotation,
    margin,
    fit.scale,
    fit.actualSrcWidth,
    fit.actualSrcHeight,
    fit.targetWidth,
    fit.targetHeight
  );
}

/** Creates the output document, guarding against a corrupted pdf-lib instance. */
async function createFittedOutputDoc(): Promise<PDFDocument> {
  // Use serialized create to prevent race conditions
  // pdf-lib's PDFDocument.create may not be thread-safe for concurrent calls
  const outDoc = await serializedPdfCreate();
  validatePdfDocument(outDoc, "PDFDocument.create() result");
  if (typeof outDoc.copyPages !== "function") {
    throw new TypeError("PDFDocument missing copyPages method");
  }
  return outDoc;
}

/** Copies the given source pages, surfacing pdf-lib's NaN corruption as a clear error. */
async function copySourcePages(
  outDoc: PDFDocument,
  srcDoc: PDFDocument,
  pageIndices: number[]
): Promise<Awaited<ReturnType<PDFDocument["copyPages"]>>> {
  try {
    // Use serialized copyPages to prevent race conditions when multiple PDFs are processed concurrently
    // pdf-lib's copyPages may not be thread-safe for concurrent calls
    return await serializedCopyPages(outDoc, srcDoc, pageIndices);
  } catch (error) {
    if (isNaNError(error)) {
      const errorMsg = error instanceof Error ? error.message : String(error);
      console.error(`copyPages NaN error: ${errorMsg}`);
      throw new TypeError(`copyPages failed with NaN error: ${errorMsg}`);
    }
    throw error;
  }
}

async function fitPdfCore(srcDoc: PDFDocument, paperSize: PaperSize, margin: number, poNumber?: string): Promise<PDFDocument> {
  const dimensions = getDimensions(paperSize);
  validateDimensions(dimensions.width, dimensions.height, `Paper dimensions for ${paperSize}`);
  validateMargin(margin);

  const firstPageFit = analyzePageFit(getFirstPageOrThrow(srcDoc), dimensions, margin);
  if (canPassThrough(firstPageFit, margin, poNumber)) {
    return srcDoc;
  }

  const outDoc = await createFittedOutputDoc();

  // Every source page must survive: carriers return multi-page labels for multi-package
  // shipments, and dropping page 2+ would mean those packages never get a label.
  const srcPages = srcDoc.getPages();
  const copiedPages = await copySourcePages(
    outDoc,
    srcDoc,
    srcPages.map((_, index) => index)
  );

  const stamp = poNumber
    ? { poNumber, font: await executeInGlobalPdfLibQueue(() => outDoc.embedFont(StandardFonts.HelveticaBold)) }
    : undefined;

  for (const [index, srcPage] of srcPages.entries()) {
    const copiedPage = copiedPages[index];
    if (!copiedPage) {
      throw new Error("Failed to copy source page");
    }

    // Serialize addPage to prevent corruption
    // Pages are added to the output document in order.
    const addOutPage = () => Promise.resolve(outDoc.addPage([dimensions.width, dimensions.height]));
    const outPage = await executeInGlobalPdfLibQueue(addOutPage); // NOSONAR

    const fit = index === 0 ? firstPageFit : analyzePageFit(srcPage, dimensions, margin);
    // Pages are drawn in order on the shared output document.
    await drawFittedPage(outDoc, outPage, copiedPage, fit, dimensions, margin); // NOSONAR

    if (stamp) {
      drawPoNumberBar(outPage, dimensions, stamp.poNumber, stamp.font);
    }
  }

  return outDoc;
}

/**
 * Fetches a PDF and fits it to the given paper size, returning a blob URL.
 *
 * @param poNumber - When set, stamps a PO number bar along the bottom of the page.
 */
export async function fitPdfToPaper(
  sourcePdfUrl: string,
  paperSize: PaperSize,
  margin: number = 0,
  signal?: AbortSignal,
  poNumber?: string
): Promise<string> {
  // Check if aborted before starting
  if (signal?.aborted) {
    throw new Error("Operation aborted");
  }

  // Use fetchPdfArrayBuffer from pdfFetchUtils (from upstream branch)
  const arrayBuffer = await fetchPdfArrayBuffer(sourcePdfUrl, { signal });
  return fitPdfToPaperFromArrayBuffer(arrayBuffer, paperSize, margin, signal, poNumber);
}

/**
 * Fits an already-fetched PDF (as raw bytes) to paper size and returns a blob URL.
 *
 * Use this instead of `fitPdfToPaper` when the source bytes are available in
 * memory — e.g. a manifest fetched once from a short-lived pre-signed S3 URL that
 * must be re-fitted on every paper-size change without re-fetching the (expiring) URL.
 *
 * @param poNumber - When set, stamps a PO# bar along the bottom of the page.
 */
export async function fitPdfToPaperFromArrayBuffer(
  arrayBuffer: ArrayBuffer,
  paperSize: PaperSize,
  margin: number = 0,
  signal?: AbortSignal,
  poNumber?: string
): Promise<string> {
  // Check if aborted before starting
  if (signal?.aborted) {
    throw new Error("Operation aborted");
  }

  // Use serialized load to ensure it goes through the global queue
  // Even for single PDFs, this prevents any potential race conditions with other pdf-lib operations
  const srcDoc = await serializedPdfLoad(arrayBuffer);

  // Check if aborted before processing
  if (signal?.aborted) {
    throw new Error("Operation aborted");
  }

  if (shouldSkipProcessing(srcDoc, paperSize, margin, poNumber)) {
    const outBytes = await srcDoc.save();
    const blob = new Blob([new Uint8Array(outBytes)], { type: "application/pdf" });
    return URL.createObjectURL(blob);
  }

  const outDoc = await fitPdfCore(srcDoc, paperSize, margin, poNumber);
  const outBytes = await outDoc.save();
  const blob = new Blob([new Uint8Array(outBytes)], { type: "application/pdf" });
  return URL.createObjectURL(blob);
}

/**
 * Fits a PDF document to paper size in memory (no blob URL creation).
 *
 * @param poNumber - When set, stamps a PO# bar along the bottom of the page.
 */
export async function fitPdfInMemory(
  srcDoc: PDFDocument,
  paperSize: PaperSize,
  margin: number = 0,
  poNumber?: string
): Promise<PDFDocument> {
  // Validate srcDoc is a valid PDFDocument
  validatePdfDocument(srcDoc, "fitPdfInMemory srcDoc");

  if (shouldSkipProcessing(srcDoc, paperSize, margin, poNumber)) {
    return srcDoc;
  }

  const result = await fitPdfCore(srcDoc, paperSize, margin, poNumber);

  // Ensure result is a valid PDFDocument before returning
  validatePdfDocument(result, "fitPdfCore result");

  // Basic validation: check that the PDFDocument has pages and valid structure
  try {
    const testPages = result.getPages();
    if (!Array.isArray(testPages) || testPages.length === 0) {
      throw new TypeError("fitPdfCore returned PDFDocument with no pages");
    }
    const testIndices = result.getPageIndices();
    if (!Array.isArray(testIndices)) {
      throw new TypeError("fitPdfCore returned PDFDocument with invalid page indices");
    }
    // Validate first page has valid dimensions
    const testFirstPage = testPages[0];
    if (testFirstPage) {
      const testWidth = testFirstPage.getWidth();
      const testHeight = testFirstPage.getHeight();
      if (!Number.isFinite(testWidth) || !Number.isFinite(testHeight) || testWidth <= 0 || testHeight <= 0) {
        console.warn(`fitPdfCore returned PDFDocument with corrupted page dimensions: width=${testWidth}, height=${testHeight}`);
      }
    }
  } catch (validationError) {
    throw new TypeError(
      `fitPdfCore returned invalid PDFDocument: ${validationError instanceof Error ? validationError.message : String(validationError)}`
    );
  }

  return result;
}
